import { useState, useEffect, useCallback, useRef } from 'react';
import { doc, onSnapshot, setDoc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useAuth } from '../contexts/AuthContext';
import logger from '../utils/logger';

/**
 * Hook for real-time Firestore sync using Firebase Auth UID
 * Data syncs automatically across all devices in real-time
 * 
 * IMPORTANT: This hook handles race conditions between local state and Firestore snapshots
 * by using timestamps and a saving flag to prevent older data from overwriting newer local changes.
 */
export function useFirestoreSync(setLojas, setProfile) {
  const { isAuthenticated, user } = useAuth();
  const [syncStatus, setSyncStatus] = useState('idle'); // idle, saving, saved, error, loading
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  
  const unsubscribeRef = useRef(null);
  const isInitialLoadRef = useRef(true);
  const lastSavedDataRef = useRef(null);
  const isSavingRef = useRef(false); // Block snapshots while saving
  const lastLocalUpdateTimestampRef = useRef(null); // Track when we last updated locally

  // Get document ID - ALWAYS use email (sanitized) for consistency across devices
  const getDocId = useCallback(() => {
    if (!user) return null;
    
    // Use email as the document ID (sanitized for Firestore)
    // This ensures the same user always accesses the same document
    const email = user.email;
    if (!email) return null;
    
    // Replace only the dot (.) with underscore to match existing Firestore document format
    // IMPORTANT: Keep @ symbol intact - this matches how data was originally saved
    const docId = String(email).replace(/\./g, '_');
    // Direct console.log for debugging (bypasses logger)
    console.log('[Firestore] Generated docId:', docId, 'from email:', email);
    return docId;
  }, [user]);

  // Check online status
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Save to Firestore with race condition protection
  const saveToFirestore = useCallback(async (lojas, profile) => {
    const docId = getDocId();
    const saveTimestamp = Date.now();
    
    console.log('[Firestore] Attempting save:', { 
      docId, 
      isAuthenticated, 
      userEmail: user?.email,
      lojasCount: lojas?.length,
      saveTimestamp
    });
    
    if (!docId || !isAuthenticated) {
      console.error('[Firestore] Cannot save - not authenticated or no docId');
      return { success: false, error: 'not_authenticated' };
    }

    // Prevent saving the same data twice
    const dataString = JSON.stringify({ lojas, profile });
    if (dataString === lastSavedDataRef.current) {
      console.log('[Firestore] Data unchanged, skipping save');
      return { success: true, cached: true };
    }

    // Set saving flag to block incoming snapshots
    isSavingRef.current = true;
    lastLocalUpdateTimestampRef.current = saveTimestamp;
    setSyncStatus('saving');

    try {
      const userDocRef = doc(db, 'users', docId);
      
      // Deep clean function to remove undefined values recursively
      const deepClean = (obj) => {
        if (obj === null || obj === undefined) return null;
        if (Array.isArray(obj)) {
          return obj.map(item => deepClean(item)).filter(item => item !== undefined);
        }
        if (typeof obj === 'object' && obj !== null) {
          const cleaned = {};
          Object.keys(obj).forEach(key => {
            const value = obj[key];
            if (value !== undefined) {
              cleaned[key] = deepClean(value);
            }
          });
          return cleaned;
        }
        return obj;
      };
      
      // Clean lojas data to remove any undefined or problematic values
      const cleanLojas = deepClean(lojas || []);
      const cleanProfile = deepClean(profile || {});
      
      const dataToSave = {
        email: user.email,
        lojas: cleanLojas,
        profile: cleanProfile,
        updatedAt: new Date().toISOString(),
        _clientTimestamp: saveTimestamp // Internal timestamp to detect our own saves
      };

      console.log('[Firestore] Saving to document:', docId, 'lojas count:', cleanLojas?.length);
      console.log('[Firestore] Data size:', JSON.stringify(dataToSave).length, 'bytes');
      
      // Log store statuses for debugging
      if (cleanLojas && cleanLojas.length > 0) {
        const aVisitar = cleanLojas.filter(l => l.status === 'a_visitar').length;
        const visitada = cleanLojas.filter(l => l.status === 'visitada' || !l.status).length;
        console.log('[Firestore] Store statuses - A Visitar:', aVisitar, 'Visitada:', visitada);
      }
      
      await setDoc(userDocRef, dataToSave, { merge: false }); // Use merge: false to ensure full overwrite
      
      lastSavedDataRef.current = dataString;
      setLastSyncTime(new Date());
      setSyncStatus('saved');
      console.log('[Firestore] Save successful! Timestamp:', saveTimestamp);
      
      // Keep saving flag true for a short period to ignore the echo snapshot
      setTimeout(() => {
        isSavingRef.current = false;
        console.log('[Firestore] Saving flag released');
      }, 1000);
      
      setTimeout(() => setSyncStatus('idle'), 2000);
      
      return { success: true };
    } catch (error) {
      console.error('[Firestore] Save error:', error);
      console.error('[Firestore] Error code:', error.code);
      console.error('[Firestore] Error message:', error.message);
      console.error('[Firestore] Full error:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
      isSavingRef.current = false;
      setSyncStatus('error');
      setTimeout(() => setSyncStatus('idle'), 3000);
      return { success: false, error: error.message };
    }
  }, [getDocId, isAuthenticated, user]);

  // Subscribe to real-time updates
  useEffect(() => {
    const docId = getDocId();
    
    // Direct console.log for debugging
    console.log('[Firestore] Auth check:', { 
      isAuthenticated, 
      docId, 
      userEmail: user?.email
    });
    
    if (!isAuthenticated || !docId) {
      console.log('[Firestore] Not setting up listener - auth or docId missing');
      // Clean up subscription if not authenticated
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
      return;
    }

    setSyncStatus('loading');
    isInitialLoadRef.current = true;

    const userDocRef = doc(db, 'users', docId);
    console.log('[Firestore] Setting up listener for document:', docId);

    // Set up real-time listener
    unsubscribeRef.current = onSnapshot(
      userDocRef,
      (docSnapshot) => {
        console.log('[Firestore] Snapshot received, exists:', docSnapshot.exists());
        
        // CRITICAL: Skip processing if we're currently saving to prevent race conditions
        if (isSavingRef.current) {
          console.log('[Firestore] Ignoring snapshot - save in progress');
          return;
        }
        
        if (docSnapshot.exists()) {
          const data = docSnapshot.data();
          const firestoreTimestamp = data._clientTimestamp || 0;
          const localTimestamp = lastLocalUpdateTimestampRef.current || 0;
          
          console.log('[Firestore] Data from snapshot:', {
            lojasCount: data.lojas?.length || 0,
            email: data.email,
            isInitialLoad: isInitialLoadRef.current
          });
          if (data.lojas && data.lojas.length > 0) {
            const aVisitar = data.lojas.filter(l => l.status === 'a_visitar').length;
            const visitada = data.lojas.filter(l => l.status === 'visitada' || !l.status).length;
            logger.log('[Firestore] Loaded - A Visitar:', aVisitar, 'Visitada:', visitada);
            
            // Log specific store status for debugging (Primer)
            const primer = data.lojas.find(l => l.name && l.name.toLowerCase().includes('primer'));
            if (primer) {
              logger.log('[Firestore] Primer from Firestore:', primer.status);
            }
          }
          
          logger.log('[Firestore] Data loaded:', { 
            lojasCount: data.lojas?.length || 0,
            email: data.email,
            updatedAt: data.updatedAt,
            firestoreTimestamp,
            localTimestamp,
            isInitialLoad: isInitialLoadRef.current
          });
          
          // Only update local state if:
          // 1. This is initial load (page just loaded), OR
          // 2. The Firestore data is from another device (different timestamp)
          const shouldUpdate = isInitialLoadRef.current || firestoreTimestamp !== localTimestamp;
          
          if (isInitialLoadRef.current) {
            // ALWAYS load data on initial load - this is critical!
            console.log('[Firestore] Applying snapshot data (initial load) - lojas count:', data.lojas?.length || 0);
            // Update local state with Firestore data
            if (data.lojas && data.lojas.length > 0) {
              setLojas(data.lojas);
              console.log('[Firestore] Successfully loaded', data.lojas.length, 'stores');
            } else {
              console.log('[Firestore] No stores found in document');
              setLojas([]);
            }
            if (data.profile) {
              setProfile(prev => ({ ...prev, ...data.profile }));
            }
          } else if (shouldUpdate && firestoreTimestamp !== localTimestamp && firestoreTimestamp > 0) {
            // Data from another device - check if it's actually different
            const currentDataString = lastSavedDataRef.current;
            const incomingDataString = JSON.stringify({ 
              lojas: data.lojas || [], 
              profile: data.profile || {} 
            });
            
            if (currentDataString !== incomingDataString) {
              logger.log('[Firestore] Applying snapshot data (from another device)');
              if (data.lojas) {
                setLojas(data.lojas);
              }
              if (data.profile) {
                setProfile(prev => ({ ...prev, ...data.profile }));
              }
            } else {
              logger.log('[Firestore] Skipping - data identical to local');
            }
          } else {
            logger.log('[Firestore] Skipping state update (our own save echo)');
          }
          
          // Update reference to prevent re-saving the same data
          lastSavedDataRef.current = JSON.stringify({ 
            lojas: data.lojas || [], 
            profile: data.profile || {} 
          });
          
          setLastSyncTime(data.updatedAt ? new Date(data.updatedAt) : new Date());
          setSyncStatus('saved');
          setTimeout(() => setSyncStatus('idle'), 1000);
        } else {
          logger.log('[Firestore] Document does not exist yet');
          // Document doesn't exist yet - create it
          if (isInitialLoadRef.current) {
            setLojas([]);
            setProfile({});
            setSyncStatus('idle');
          }
        }
        
        isInitialLoadRef.current = false;
      },
      (error) => {
        logger.error('[Firestore] Listener error:', error.code, error.message);
        setSyncStatus('error');
        setTimeout(() => setSyncStatus('idle'), 3000);
      }
    );

    // Cleanup subscription on unmount or auth change
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [isAuthenticated, getDocId, setLojas, setProfile]);

  // Reset on logout
  useEffect(() => {
    if (!isAuthenticated) {
      isInitialLoadRef.current = true;
      lastSavedDataRef.current = null;
      setLojas([]);
      setProfile({});
    }
  }, [isAuthenticated, setLojas, setProfile]);

  return {
    syncStatus,
    lastSyncTime,
    isOnline,
    backendAvailable: true, // Firestore is always "available" (works offline too)
    userId: user?.id || null,
    saveToFirestore
  };
}
